import type { PaletteColor } from "@brexhq/metal-foundation";
import { darkTheme as darklibraryTheme } from "@brexhq/metal-foundation";
import { palette } from "@brexhq/metal-foundation-shared";

import { defaultTheme, LegacyThemeColors } from "@brexhq/shared/styles/theming";

const { colors } = defaultTheme;
const { colors: darkColors } = darklibraryTheme;
const gradientPurpleOrange =
  "linear-gradient(134.2deg, #DD9478 -20.6%, #DA734E 20.06%, #8D2C30 60.26%, #530022 100%)";
export const PEACH_LIGHT = "#FFBC96";
export const CYAN_LIGHT = "#91ECEA";
export const PEACH_DARK = "#E8905E";
export const CYAN_DARK = "#4BB4B1";
const gradientPeachCyanLight = `linear-gradient(123deg, ${PEACH_LIGHT} 0%, ${CYAN_LIGHT} 99.04%)`;
const gradientCyanPeachDark = `linear-gradient(123deg, ${CYAN_DARK} 16.69%, ${PEACH_DARK} 78.17%)`;
const gradientBlue =
  "linear-gradient(311.74deg, #B3D6E8 -27.01%, #ADCFE1 -21.65%, #566A75 55.94%, #386B85 115.56%)";
const gradientAI =
  "linear-gradient(91deg, #F6F8FE -4.36%, #FFF -0.1%, #3A72E9 107.06%)";

export interface CardThemeProperties {
  background: PaletteColor;
  textPrimary: PaletteColor;
  textSecondary: PaletteColor;
  icons: PaletteColor;
  borders: PaletteColor;
}

export interface StandardThemeProperties extends CardThemeProperties {
  backgroundSecondary: PaletteColor;
  accent: PaletteColor;
  gradientPurpleOrange: string;
  gradientPeachCyan: string;
  gradientBlue: string;
  gradientAI: string;
}

export interface ModuleTheme {
  standard: StandardThemeProperties;
  // Card is an additional set of colors that gets used inside cards that are used inside the selected theme
  card: CardThemeProperties;
}

type NewModuleThemeType = "White" | "Neutral" | "Dark";

export type ModuleThemeType =
  | "PrimaryWhite"
  | "PrimaryPaper"
  | "PrimaryGray"
  | "PrimaryInk"
  | "PrimaryDark"
  | "SecondaryGreen"
  | "SecondaryMaroon"
  | "SecondaryBlue"
  | "SecondaryPurple"
  | "TertiaryYellow"
  | "TertiaryBlue"
  | "TertiaryOrange"
  | "TertiaryGreen"
  | NewModuleThemeType;

const darkThemes: ModuleThemeType[] = [
  "PrimaryInk",
  "PrimaryDark",
  "SecondaryMaroon",
  "SecondaryGreen",
  "SecondaryPurple",
  "SecondaryBlue",
];

export const isDarkTheme = (theme: ModuleThemeType): boolean => {
  return darkThemes.includes(theme);
};

export const isDarkThemeOnDarkBackground = (
  childTheme: ModuleThemeType,
  parentTheme: ModuleThemeType,
): boolean => {
  return (
    (childTheme === "PrimaryInk" || childTheme === "PrimaryDark") &&
    (parentTheme === "PrimaryInk" || parentTheme === "PrimaryDark")
  );
};

const newModuleThemes: Record<NewModuleThemeType, ModuleTheme> = {
  White: {
    standard: {
      background: palette.neutrals.white,
      backgroundSecondary: palette.neutrals.coolGrey3,
      textPrimary: palette.neutrals.coolGrey12,
      textSecondary: palette.neutrals.coolGrey11,
      icons: palette.neutrals.coolGrey9,
      borders: colors.border.default,
      accent: palette.orange[8],
      gradientPurpleOrange,
      gradientPeachCyan: gradientCyanPeachDark,
      gradientBlue,
      gradientAI,
    },
    card: {
      background: palette.neutrals.coolGrey3,
      textPrimary: palette.neutrals.coolGrey12,
      textSecondary: palette.neutrals.coolGrey11,
      icons: palette.neutrals.coolGrey9,
      borders: colors.border.default,
    },
  },
  Neutral: {
    standard: {
      background: palette.neutrals.coolGrey3,
      backgroundSecondary: palette.neutrals.coolGrey2,
      textPrimary: palette.neutrals.coolGrey12,
      textSecondary: palette.neutrals.coolGrey11,
      icons: palette.neutrals.coolGrey9,
      borders: colors.border.default,
      accent: palette.orange[8],
      gradientPurpleOrange,
      gradientPeachCyan: gradientCyanPeachDark,
      gradientBlue,
      gradientAI,
    },
    card: {
      background: palette.neutrals.white,
      textPrimary: palette.neutrals.coolGrey12,
      textSecondary: palette.neutrals.coolGrey11,
      icons: palette.neutrals.coolGrey9,
      borders: colors.border.default,
    },
  },
  Dark: {
    standard: {
      background: palette.slateBlue[12],
      backgroundSecondary: palette.neutrals.coolGrey12,
      textPrimary: palette.neutrals.coolGrey1,
      textSecondary: palette.neutrals.coolGrey7,
      icons: palette.neutrals.coolGrey9,
      borders: darkColors.border.inverse.default,
      accent: palette.orange[8],
      gradientPurpleOrange,
      gradientPeachCyan: gradientPeachCyanLight,
      gradientBlue,
      gradientAI,
    },
    card: {
      background: palette.neutrals.coolGrey12,
      textPrimary: palette.neutrals.white,
      textSecondary: palette.neutrals.coolGrey7,
      icons: palette.neutrals.coolGrey9,
      borders: darkColors.border.inverse.default,
    },
  },
};

export const moduleThemes: Record<ModuleThemeType, ModuleTheme> = {
  ...newModuleThemes,
  PrimaryWhite: {
    ...newModuleThemes.White,
  },
  PrimaryPaper: {
    ...newModuleThemes.Neutral,
  },
  PrimaryGray: {
    ...newModuleThemes.Neutral,
  },
  PrimaryInk: {
    ...newModuleThemes.Dark,
  },
  PrimaryDark: {
    ...newModuleThemes.Dark,
  },
  // TODO (DOTCOM-169): Remove all themes below this line
  SecondaryGreen: {
    standard: {
      background: LegacyThemeColors.TRUST_GREEN,
      backgroundSecondary: LegacyThemeColors.TRUST_GREEN,
      textPrimary: colors.content.inverse.primary,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
      accent: LegacyThemeColors.ENERGY_YELLOW,
      gradientPurpleOrange: colors.content.inverse.primary,
      gradientPeachCyan: colors.content.inverse.primary,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.PAPER_BACKGROUND,
      textPrimary: colors.content.primary,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
    },
  },
  SecondaryMaroon: {
    standard: {
      background: LegacyThemeColors.TRUST_MAROON,
      backgroundSecondary: LegacyThemeColors.TRUST_MAROON,
      textPrimary: colors.content.inverse.primary,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
      accent: LegacyThemeColors.ENERGY_ORANGE,
      gradientPurpleOrange: colors.content.inverse.primary,
      gradientPeachCyan: colors.content.inverse.primary,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.PAPER_BACKGROUND,
      textPrimary: colors.content.primary,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
    },
  },
  SecondaryBlue: {
    standard: {
      background: LegacyThemeColors.TRUST_BLUE,
      backgroundSecondary: LegacyThemeColors.TRUST_BLUE,
      textPrimary: colors.content.inverse.primary,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
      accent: LegacyThemeColors.ENERGY_GREEN,
      gradientPurpleOrange: colors.content.inverse.primary,
      gradientPeachCyan: colors.content.inverse.primary,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.PAPER_BACKGROUND,
      textPrimary: colors.content.primary,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
    },
  },
  SecondaryPurple: {
    standard: {
      background: LegacyThemeColors.TRUST_PURPLE,
      backgroundSecondary: LegacyThemeColors.TRUST_PURPLE,
      textPrimary: colors.content.inverse.primary,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
      accent: LegacyThemeColors.ENERGY_BLUE,
      gradientPurpleOrange: colors.content.inverse.primary,
      gradientPeachCyan: colors.content.inverse.primary,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.PAPER_BACKGROUND,
      textPrimary: colors.content.primary,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
    },
  },
  TertiaryYellow: {
    standard: {
      background: LegacyThemeColors.ENERGY_YELLOW,
      backgroundSecondary: LegacyThemeColors.ENERGY_YELLOW,
      textPrimary: LegacyThemeColors.TRUST_GREEN,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
      accent: LegacyThemeColors.ENERGY_BLUE,
      gradientPurpleOrange: LegacyThemeColors.TRUST_GREEN,
      gradientPeachCyan: LegacyThemeColors.TRUST_GREEN,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.TRUST_GREEN,
      textPrimary: LegacyThemeColors.ENERGY_YELLOW,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
    },
  },
  TertiaryBlue: {
    standard: {
      background: LegacyThemeColors.ENERGY_BLUE,
      backgroundSecondary: LegacyThemeColors.ENERGY_BLUE,
      textPrimary: LegacyThemeColors.TRUST_PURPLE,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
      accent: LegacyThemeColors.ENERGY_BLUE,
      gradientPurpleOrange: LegacyThemeColors.TRUST_PURPLE,
      gradientPeachCyan: LegacyThemeColors.TRUST_PURPLE,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.TRUST_PURPLE,
      textPrimary: LegacyThemeColors.ENERGY_BLUE,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
    },
  },
  TertiaryOrange: {
    standard: {
      background: LegacyThemeColors.ENERGY_ORANGE,
      backgroundSecondary: LegacyThemeColors.ENERGY_ORANGE,
      textPrimary: LegacyThemeColors.TRUST_MAROON,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
      accent: LegacyThemeColors.ENERGY_BLUE,
      gradientPurpleOrange: LegacyThemeColors.TRUST_MAROON,
      gradientPeachCyan: LegacyThemeColors.TRUST_MAROON,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.TRUST_MAROON,
      textPrimary: LegacyThemeColors.ENERGY_ORANGE,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
    },
  },
  TertiaryGreen: {
    standard: {
      background: LegacyThemeColors.ENERGY_GREEN,
      backgroundSecondary: LegacyThemeColors.ENERGY_GREEN,
      textPrimary: LegacyThemeColors.TRUST_BLUE,
      textSecondary: colors.content.secondary,
      icons: colors.content.tertiary,
      borders: colors.border.default,
      accent: LegacyThemeColors.ENERGY_BLUE,
      gradientPurpleOrange: LegacyThemeColors.TRUST_BLUE,
      gradientPeachCyan: LegacyThemeColors.TRUST_BLUE,
      gradientBlue: colors.content.inverse.primary,
      gradientAI: colors.content.inverse.primary,
    },
    card: {
      background: LegacyThemeColors.TRUST_BLUE,
      textPrimary: LegacyThemeColors.ENERGY_GREEN,
      textSecondary: colors.content.inverse.secondary,
      icons: colors.content.inverse.tertiary,
      borders: colors.border.inverse.default,
    },
  },
} as const;

export const allModuleTheme = Object.keys(moduleThemes) as ModuleThemeType[];
